import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@heroui/button";
import { AlertTriangle, Loader2 } from "lucide-react";

import { ordersService } from "../../services/ordersService";
import { OrderDetail } from "../../types/orders";
import PageTitle from "../../components/common/PageTitle";
import OrderInfo from "../../components/orders/OrderInfo";
import OrderItems from "../../components/orders/OrderItems";
import OrderActions from "../../components/orders/OrderActions";
import OrderHistory from "../../components/orders/OrderHistory";
import OrderStatusUpdate from "../../components/orders/OrderStatusUpdate";

const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const orderData = await ordersService.getOrderById(parseInt(id));

        setOrder(orderData);
      } catch (err) {
        console.error("Failed to fetch order:", err);
        setError("Failed to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handleStatusUpdate = async (statusId: number, notes?: string) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      await ordersService.updateOrderStatus(parseInt(id), {
        status_id: statusId,
        notes,
      });

      const refreshedOrder = await ordersService.getOrderById(parseInt(id));

      setOrder(refreshedOrder);

      setShowStatusUpdate(false);
    } catch (err) {
      console.error("Failed to update order status:", err);
      setError("Failed to update order status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentUpdate = async (isPaid: boolean) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      await ordersService.updateOrderPayment(parseInt(id), {
        is_paid: isPaid,
      });

      const refreshedOrder = await ordersService.getOrderById(parseInt(id));

      setOrder(refreshedOrder);
    } catch (err) {
      console.error("Failed to update payment status:", err);
      setError("Failed to update payment status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  if (loading && !order) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg max-w-md">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Error Loading Order
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error || "The order could not be found or has been deleted."}
          </p>
          <Button color="primary" onPress={() => navigate("/orders")}>
            Return to Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        actions={
          <div className="flex gap-2">
            <Button
              color="primary"
              variant="light"
              onPress={() => window.print()}
            >
              Print
            </Button>
            <Button color="primary" onPress={() => setShowStatusUpdate(true)}>
              Update Status
            </Button>
          </div>
        }
        subtitle={`Created on ${new Date(order.created_at).toLocaleString()}`}
        title={`Order #${order.order_number}`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <OrderInfo formatCurrency={formatCurrency} order={order} />
          <OrderItems formatCurrency={formatCurrency} order={order} />
        </div>
        <div className="space-y-6">
          <OrderActions
            loading={loading}
            order={order}
            onUpdatePayment={handlePaymentUpdate}
          />
          <OrderHistory history={order.history} />
        </div>
      </div>

      {showStatusUpdate && (
        <OrderStatusUpdate
          currentStatusId={order.status_id}
          loading={loading}
          onCancel={() => setShowStatusUpdate(false)}
          onUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default OrderDetailsPage;
